/**
 * LinkedInTargetingPanel
 * Platform-specific targeting UI for LinkedIn Ads
 * Includes company targeting, job targeting, skills, and professional demographics
 */

import React, { useState, useEffect } from 'react';
import { Building2, Briefcase, GraduationCap, Target, ChevronDown, ChevronRight, AlertTriangle, Loader2 } from 'lucide-react';
import InterestSearch from './InterestSearch';
import LocationSearch from './LocationSearch';
import AudienceEstimate from './AudienceEstimate';

const companySizes = [
    { id: 'SIZE_1', name: 'Self-employed (1)' },
    { id: 'SIZE_2_10', name: '2-10 employees' },
    { id: 'SIZE_11_50', name: '11-50 employees' },
    { id: 'SIZE_51_200', name: '51-200 employees' },
    { id: 'SIZE_201_500', name: '201-500 employees' },
    { id: 'SIZE_501_1000', name: '501-1,000 employees' },
    { id: 'SIZE_1001_5000', name: '1,001-5,000 employees' },
    { id: 'SIZE_5001_10000', name: '5,001-10,000 employees' },
    { id: 'SIZE_10001_PLUS', name: '10,001+ employees' }
];

const seniorities = [
    { id: 'ENTRY', name: 'Entry' },
    { id: 'SENIOR', name: 'Senior' },
    { id: 'MANAGER', name: 'Manager' },
    { id: 'DIRECTOR', name: 'Director' },
    { id: 'VP', name: 'VP' },
    { id: 'CXO', name: 'CXO' },
    { id: 'OWNER', name: 'Owner/Partner' }
];

export default function LinkedInTargetingPanel({
    brandId,
    userId,
    targeting = {},
    onChange,
    isConnected,
    accountId = null
}) {
    const [industries, setIndustries] = useState([]);
    const [jobFunctions, setJobFunctions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        locations: true,
        company: true,
        job: true,
        skills: false,
        audiences: false
    });

    // Constraint warning: Cannot use job titles + seniority together
    const hasJobTitles = (targeting.detailedTargeting?.linkedin?.job?.titles?.length || 0) > 0;
    const hasSeniority = (targeting.detailedTargeting?.linkedin?.job?.seniorities?.length || 0) > 0;
    const hasConstraintViolation = hasJobTitles && hasSeniority;

    useEffect(() => {
        if (isConnected) {
            loadOptions();
        }
    }, [isConnected]);

    const loadOptions = async () => {
        setLoadingOptions(true);
        try {
            const response = await fetch(`/api/targeting/linkedin/options?brandId=${brandId}&userId=${userId}`);
            if (response.ok) {
                const data = await response.json();
                setIndustries(data.industries || []);
                setJobFunctions(data.jobFunctions || []);
            }
        } catch (err) {
            console.error('Error loading LinkedIn options:', err);
        } finally {
            setLoadingOptions(false);
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const updateTargeting = (key, value) => {
        onChange({ ...targeting, [key]: value });
    };

    const updateLinkedInTargeting = (category, field, value) => {
        onChange({
            ...targeting,
            detailedTargeting: {
                ...targeting.detailedTargeting,
                linkedin: {
                    ...targeting.detailedTargeting?.linkedin,
                    [category]: {
                        ...targeting.detailedTargeting?.linkedin?.[category],
                        [field]: value
                    }
                }
            }
        });
    };

    const handleLocationSelect = (loc) => {
        const current = targeting.locations?.included || [];
        updateTargeting('locations', { ...targeting.locations, included: [...current, loc] });
    };

    const handleLocationRemove = (loc) => {
        const current = targeting.locations?.included || [];
        updateTargeting('locations', { ...targeting.locations, included: current.filter(l => l.id !== loc.id) });
    };

    const handleJobTitleSelect = (item) => {
        const current = targeting.detailedTargeting?.linkedin?.job?.titles || [];
        updateLinkedInTargeting('job', 'titles', [...current, item]);
    };

    const handleJobTitleRemove = (item) => {
        const current = targeting.detailedTargeting?.linkedin?.job?.titles || [];
        updateLinkedInTargeting('job', 'titles', current.filter(t => t.id !== item.id));
    };

    const handleSkillSelect = (item) => {
        const current = targeting.detailedTargeting?.linkedin?.skills || [];
        onChange({
            ...targeting,
            detailedTargeting: {
                ...targeting.detailedTargeting,
                linkedin: { ...targeting.detailedTargeting?.linkedin, skills: [...current, item] }
            }
        });
    };

    const handleSkillRemove = (item) => {
        const current = targeting.detailedTargeting?.linkedin?.skills || [];
        onChange({
            ...targeting,
            detailedTargeting: {
                ...targeting.detailedTargeting,
                linkedin: { ...targeting.detailedTargeting?.linkedin, skills: current.filter(s => s.id !== item.id) }
            }
        });
    };

    const toggleSelection = (category, field, item, isSelected) => {
        const current = targeting.detailedTargeting?.linkedin?.[category]?.[field] || [];
        if (isSelected) {
            updateLinkedInTargeting(category, field, current.filter(i => i.id !== item.id));
        } else {
            updateLinkedInTargeting(category, field, [...current, item]);
        }
    };

    return (
        <div className="linkedin-targeting-panel">
            {/* Constraint Warning */}
            {hasConstraintViolation && (
                <div className="constraint-warning">
                    <AlertTriangle size={16} />
                    <span>LinkedIn does not allow combining Job Title with Job Seniority targeting. Remove one to proceed.</span>
                </div>
            )}

            {/* Locations */}
            <div className="targeting-section">
                <button type="button" className="section-header" onClick={() => toggleSection('locations')}>
                    <Target size={16} />
                    <span className="section-title">Locations</span>
                    {(targeting.locations?.included?.length || 0) > 0 && (
                        <span className="section-count">{targeting.locations.included.length}</span>
                    )}
                    {expandedSections.locations ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {expandedSections.locations && (
                    <div className="section-content">
                        <LocationSearch
                            platform="linkedin"
                            brandId={brandId}
                            userId={userId}
                            selectedLocations={targeting.locations?.included || []}
                            onSelect={handleLocationSelect}
                            onRemove={handleLocationRemove}
                            showExclusions={false}
                            placeholder="Search locations..."
                        />
                    </div>
                )}
            </div>

            {/* Company Targeting */}
            <div className="targeting-section">
                <button type="button" className="section-header" onClick={() => toggleSection('company')}>
                    <Building2 size={16} />
                    <span className="section-title">Company</span>
                    {expandedSections.company ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {expandedSections.company && (
                    <div className="section-content">
                        {/* Industries */}
                        <div className="form-group">
                            <label>Industries</label>
                            <div className="checkbox-grid">
                                {industries.slice(0, 12).map(ind => {
                                    const isSelected = targeting.detailedTargeting?.linkedin?.company?.industries?.some(i => i.id === ind.id);
                                    return (
                                        <label key={ind.id} className="checkbox-item">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => toggleSelection('company', 'industries', ind, isSelected)}
                                            />
                                            <span>{ind.name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Company Sizes */}
                        <div className="form-group">
                            <label>Company Size</label>
                            <div className="checkbox-grid">
                                {companySizes.map(size => {
                                    const isSelected = targeting.detailedTargeting?.linkedin?.company?.sizes?.some(s => s.id === size.id);
                                    return (
                                        <label key={size.id} className="checkbox-item">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => toggleSelection('company', 'sizes', size, isSelected)}
                                            />
                                            <span>{size.name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Job Targeting */}
            <div className="targeting-section">
                <button type="button" className="section-header" onClick={() => toggleSection('job')}>
                    <Briefcase size={16} />
                    <span className="section-title">Job</span>
                    {expandedSections.job ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {expandedSections.job && (
                    <div className="section-content">
                        {/* Job Titles */}
                        <div className="form-group">
                            <label>Job Titles {hasSeniority && <span className="label-warning">(Cannot combine with Seniority)</span>}</label>
                            <InterestSearch
                                platform="linkedin"
                                type="title"
                                brandId={brandId}
                                userId={userId}
                                selectedItems={targeting.detailedTargeting?.linkedin?.job?.titles || []}
                                onSelect={handleJobTitleSelect}
                                onRemove={handleJobTitleRemove}
                                placeholder="Search job titles..."
                                disabled={hasSeniority}
                            />
                        </div>

                        {/* Job Functions */}
                        <div className="form-group">
                            <label>Job Functions</label>
                            <div className="checkbox-grid">
                                {jobFunctions.slice(0, 12).map(fn => {
                                    const isSelected = targeting.detailedTargeting?.linkedin?.job?.functions?.some(f => f.id === fn.id);
                                    return (
                                        <label key={fn.id} className="checkbox-item">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => toggleSelection('job', 'functions', fn, isSelected)}
                                            />
                                            <span>{fn.name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Seniority */}
                        <div className="form-group">
                            <label>Seniority {hasJobTitles && <span className="label-warning">(Cannot combine with Job Titles)</span>}</label>
                            <div className="checkbox-grid">
                                {seniorities.map(sen => {
                                    const isSelected = targeting.detailedTargeting?.linkedin?.job?.seniorities?.some(s => s.id === sen.id);
                                    return (
                                        <label key={sen.id} className={`checkbox-item ${hasJobTitles ? 'disabled' : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => !hasJobTitles && toggleSelection('job', 'seniorities', sen, isSelected)}
                                                disabled={hasJobTitles}
                                            />
                                            <span>{sen.name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Skills */}
            <div className="targeting-section">
                <button type="button" className="section-header" onClick={() => toggleSection('skills')}>
                    <GraduationCap size={16} />
                    <span className="section-title">Skills</span>
                    {(targeting.detailedTargeting?.linkedin?.skills?.length || 0) > 0 && (
                        <span className="section-count">{targeting.detailedTargeting.linkedin.skills.length}</span>
                    )}
                    {expandedSections.skills ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {expandedSections.skills && (
                    <div className="section-content">
                        <InterestSearch
                            platform="linkedin"
                            type="skill"
                            brandId={brandId}
                            userId={userId}
                            selectedItems={targeting.detailedTargeting?.linkedin?.skills || []}
                            onSelect={handleSkillSelect}
                            onRemove={handleSkillRemove}
                            placeholder="Search skills..."
                        />
                    </div>
                )}
            </div>

            {/* Audience Estimate */}
            <div className="estimate-section">
                <AudienceEstimate
                    platform="linkedin"
                    brandId={brandId}
                    userId={userId}
                    targeting={targeting}
                />
            </div>

            <style>{`
                .linkedin-targeting-panel { display: flex; flex-direction: column; gap: 0.75rem; }
                .constraint-warning { display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1rem; background: color-mix(in srgb, var(--color-error) 15%, transparent); color: var(--color-error); border-radius: 8px; font-size: 0.8125rem; }
                .targeting-section { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 8px; }
                .section-header { display: flex; align-items: center; gap: 0.5rem; width: 100%; padding: 0.75rem 1rem; background: var(--color-bg-primary); border: none; text-align: left; cursor: pointer; color: var(--color-text-primary); }
                .section-title { flex: 1; font-size: 0.875rem; font-weight: 500; color: var(--color-text-primary); }
                .section-count { padding: 0.125rem 0.5rem; background: var(--color-primary); color: var(--color-text-inverted); border-radius: 10px; font-size: 0.75rem; }
                .section-content { padding: 1rem; border-top: 1px solid var(--color-border); }
                .form-group { margin-bottom: 1rem; }
                .form-group:last-child { margin-bottom: 0; }
                .form-group label { display: block; font-size: 0.8125rem; font-weight: 500; color: var(--color-text-tertiary); margin-bottom: 0.5rem; }
                .label-warning { color: var(--color-error); font-weight: normal; font-size: 0.75rem; }
                .checkbox-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem; }
                .checkbox-item { display: flex; align-items: center; gap: 0.5rem; padding: 0.375rem 0.5rem; background: var(--color-bg-primary); border-radius: 4px; font-size: 0.8125rem; cursor: pointer; color: var(--color-text-primary); }
                .checkbox-item.disabled { opacity: 0.5; cursor: not-allowed; }
                .checkbox-item input { width: 14px; height: 14px; }
                .estimate-section { margin-top: 0.5rem; }
            `}</style>
        </div>
    );
}
